import { useEffect, useState } from 'react';

import { ACCEPT_TYPE_IMAGE } from 'lib/form/file-dropper/file-dropper.constants';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper';
import {
  LANGUAGE_CODE_EN_AU,
  POSITION_RIGHT,
  POSITION_TOP,
} from 'global/constants';
import { EditProps, MultipleChoiceOption } from '../multiple-choice.constants';
import {
  generateTextFieldKey,
  updateOptionImageData,
  updateOptionText,
} from '../settings/multiple-choice-settings.utils';
import { useMultipleChoiceUpdate } from '../settings/multiple-choice-settings.hooks';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useInputFieldState } from '../../component.hooks';
import {
  canShowOptionLetter,
  getImageCropData,
  getOptionsPerColumn,
} from '../multiple-choice.utils';
import { MultipleChoiceLetter } from '../multiple-choice';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { ComponentActions } from '../../components.style';
import ComponentAltTextButton from '../../shared/component-alt-text/component-alt-text-button';
import ComponentImageModal from '../../shared/component-image-modal/component-image-modal';
import {
  MultipleChoiceEditBox,
  MultipleChoiceTextContent,
} from '../multiple-choice-edit.style';
import MultipleChoiceImage from '../image/multiple-choice-image';
import MultipleChoiceEditTextField from '../text/multiple-choice-edit-text-field';
import * as Styled from './multiple-choice-edit-text-image.style';

type Props = {
  index: number;
  isUpdating: boolean;
  option: MultipleChoiceOption;
  setIsUpdating: (state: boolean) => void;
};

const MultipleChoiceEditTextImage = ({
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  sectionId,
  id,
  type,
  sort,
  index,
  isUpdating,
  option,
  version,
  setIsUpdating,
}: Props & EditProps) => {
  const uploadKey = `image-${index}`;

  const [elementRef, setRef] = useState<HTMLDivElement | null>(null);

  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { theme } = useActivityBuilderTheme();
  const { file, clearFile } = useFileDropperContext(uploadKey);

  const { __typename, ...restProperties } = properties.multipleChoice;
  const options = restProperties.options;

  const { loading, handleUpdate, handleFileUpload } = useMultipleChoiceUpdate({
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
    language: LANGUAGE_CODE_EN_AU,
    version,
  });

  const { setEditorStateToUpdate } = useInputFieldState<string>({
    onChange: data => {
      if (option.itemId) {
        handleUpdate({
          ...properties,
          multipleChoice: {
            ...properties.multipleChoice,
            options: updateOptionText(options, option.itemId, data),
          },
        });
      }
    },
  });

  useEffect(() => {
    if (file) {
      setIsUpdating(true);
    }
  }, [file, setIsUpdating]);

  const imageContent =
    option.image === null ? (
      <Styled.MultipleChoiceEditTextImageUpload ref={setRef}>
        <FileDropperImage
          accept={[ACCEPT_TYPE_IMAGE]}
          hidePlaceholder
          showAcceptExtensions={false}
          disabled={isUpdating}
          name={uploadKey}
          additionalLabel={
            elementRef
              ? `${elementRef.offsetWidth * 2}px x ${
                  elementRef.offsetHeight * 2
                }px (W x H)`
              : '...'
          }
          onChange={(key, fileName) => {
            if (option.itemId) {
              handleFileUpload({
                id: option.itemId,
                key,
                fileName,
                properties,
                onCompleted: () => {
                  clearFile();
                  setIsUpdating(false);
                },
              });
            }
          }}
          onFileRemoved={() => setIsUpdating(false)}
        />
      </Styled.MultipleChoiceEditTextImageUpload>
    ) : (
      <Styled.MultipleChoiceEditTextImageImage
        ref={setRef}
        id={`multi-choice-${option.itemId}`}
      >
        <Styled.MultipleChoiceEditTextImageButton
          type="button"
          aria-haspopup="true"
          aria-label={`Click to edit ${option.image.alt} image`}
          onClick={openModal}
        >
          <MultipleChoiceImage option={option} />
        </Styled.MultipleChoiceEditTextImageButton>
        <ComponentActions>
          <ComponentAltTextButton
            hasAltText={!!option.image.alt}
            onClick={openModal}
          />
        </ComponentActions>
      </Styled.MultipleChoiceEditTextImageImage>
    );

  return (
    <MultipleChoiceEditBox
      $itemsPerRow={getOptionsPerColumn(properties.multipleChoice)}
      $minHeight={properties.multipleChoice.optionMinHeight || 80}
    >
      {isVisible && elementRef && (
        <ComponentImageModal
          loading={loading}
          containerDimensions={{
            width: elementRef.offsetWidth,
            height: elementRef.offsetHeight,
          }}
          image={option.image || undefined}
          altText={option.image?.alt}
          sourceUrl={option.sourceUrl}
          cropData={getImageCropData(option.imageLayout, option.cropData)}
          closeModal={closeModal}
          onUpdateData={data =>
            handleUpdate(
              {
                ...properties,
                multipleChoice: {
                  ...properties.multipleChoice,
                  options: updateOptionImageData({
                    options,
                    id: option.itemId,
                    altText: data.altText,
                    cropData: data.cropData,
                    imageLayout: data.imageLayout,
                    image: data.image || null,
                    sourceUrl: null,
                  }),
                },
              },
              {
                isSaveImmediate: true,
                onCompleted: () => {
                  setIsUpdating(false);
                  clearFile();
                  closeModal();
                },
              }
            )
          }
        />
      )}
      <Styled.MultipleChoiceEditTextImage
        data-selector="multiple-choice-options"
        $minHeight={properties.multipleChoice.optionMinHeight || 80}
        $imagePosition={properties.multipleChoice.imagePosition}
      >
        {properties.multipleChoice.imagePosition === POSITION_TOP &&
          imageContent}

        <Styled.MultipleChoiceEditTextImageColumn>
          <MultipleChoiceTextContent
            data-selector="multiple-choice-input"
            $hasPadding={
              properties.multipleChoice.imagePosition === POSITION_RIGHT
            }
          >
            {canShowOptionLetter(
              properties.multipleChoice.showOptionLetter
            ) && (
              <MultipleChoiceLetter
                data-selector="multiple-choice-letter"
                index={index}
              />
            )}
            <MultipleChoiceEditTextField
              key={generateTextFieldKey(theme.font.medium.size, properties)}
              index={index}
              text={option.text}
              onChange={value => setEditorStateToUpdate(value)}
            />
          </MultipleChoiceTextContent>
        </Styled.MultipleChoiceEditTextImageColumn>

        {properties.multipleChoice.imagePosition !== POSITION_TOP &&
          imageContent}
      </Styled.MultipleChoiceEditTextImage>
    </MultipleChoiceEditBox>
  );
};

export default MultipleChoiceEditTextImage;
